// src/pages/auth/SignIn.js
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import loginImage from '../../assets/images/profile.png';
import { authAPI } from '../../api/apiConfig';

export default function SignIn() {
    const [mobile, setMobile] = useState('+91-9490633344'); // Pre-filled admin mobile
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        // Validate mobile format
        if (!mobile || !mobile.startsWith('+')) {
            setError('Please enter a valid mobile number with country code (e.g., +91-9876543210)');
            return;
        }
        
        // ONLY accept admin mobile for now
        if (mobile !== '+91-9490633344') {
            setError('Access Denied! Only admin credentials are allowed.');
            return;
        }
        
        setLoading(true);
        
        try {
            // Call API to send OTP
            const response = await authAPI.sendOTP(mobile);
            
            if (response.success) {
                // Save mobile for OTP verification
                localStorage.setItem('adminMobile', mobile);
                
                // Navigate to OTP verification page
                navigate('/verify-otp');
            } else {
                setError(response.message || 'Failed to send OTP');
            }
        } catch (error) {
            console.error('Send OTP error:', error);
            setError(error.message || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = () => {
        alert('Admin login only available with mobile: +91-9490633344');
    };

    const handleAppleSignIn = () => {
        alert('Admin login only available with mobile: +91-9490633344');
    };

    return (
        <div className="container-fluid login-container" style={{ minHeight: '100vh' }}>
            <div className="row">
                {/* Left Side: Form */}
                <div className="col-lg-6 d-flex align-items-center justify-content-center">
                    <div className="w-100 px-lg-5 px-4 py-4" style={{ maxWidth: '420px' }}>
                        <h3 className="mb-3 fw-bold">Admin Login 👑</h3>
                        <p className="text-muted mb-4">Admin access only via mobile OTP</p>
                        
                        {/* Admin Credentials Notice */}
                        <div className="alert alert-warning mb-3">
                            <small>
                                <strong>Admin Credentials Only:</strong><br/>
                                Mobile: <code>+91-9490633344</code><br/>
                                OTP: <code>123456</code>
                            </small>
                        </div>
                        
                        {/* Error Message */}
                        {error && (
                            <div className="alert alert-danger">
                                <small>{error}</small>
                            </div>
                        )}
                        
                        {/* Google / Apple Sign In - Disabled */}
                        <div className="d-grid gap-2 mb-3">
                            <button 
                                className="btn btn-outline-dark d-flex align-items-center justify-content-center gap-2"
                                onClick={handleGoogleSignIn}
                                disabled={loading}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2a9.96 9.96 0 0 1 6.29 2.226a1 1 0 0 1 .04 1.52l-1.51 1.362a1 1 0 0 1 -1.265 .06a6 6 0 1 0 2.103 6.836l.001 -.004h-3.66a1 1 0 0 1 -.992 -.883l-.007 -.117v-2a1 1 0 0 1 1 -1h6.945a1 1 0 0 1 .994 .89c.04 .367 .061 .737 .061 1.11c0 5.523 -4.477 10 -10 10s-10 -4.477 -10 -10s4.477 -10 10 -10z" />
                                </svg>
                                Sign in with Google
                            </button>
                            <button 
                                className="btn btn-dark d-flex align-items-center justify-content-center gap-2"
                                onClick={handleAppleSignIn}
                                disabled={loading}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M15.079 5.999l.239 .012c1.43 .097 3.434 1.013 4.508 2.586a1 1 0 0 1 -.344 1.44c-.05 .028 -.372 .158 -.497 .217a4.15 4.15 0 0 0 -.722 .431c-.614 .461 -.948 1.009 -.942 1.694c.01 .885 .339 1.454 .907 1.846c.208 .143 .436 .253 .666 .33c.126 .043 .426 .116 .444 .122a1 1 0 0 1 .662 .942c0 2.621 -3.04 6.381 -5.286 6.381c-.790 0 -1.272 -.091 -1.983 -.315l-.098 -.031c-.463 -.146 -.702 -.192 -1.133 -.192c-.520 0 -.863 .06 -1.518 .237l-.197 .053c-.575 .153 -.964 .226 -1.5 .248c-2.749 0 -5.285 -5.093 -5.285 -9.072c0 -3.87 1.786 -6.92 5.286 -6.92c.297 0 .598 .045 .909 .128c.403 .107 .774 .26 1.296 .508c.787 .374 .948 .44 1.009 .44h.016c.03 -.003 .128 -.047 1.056 -.457c1.061 -.467 1.864 -.685 2.746 -.616l-.24 -.012z" />
                                    <path d="M14 1a1 1 0 0 1 1 1a3 3 0 0 1 -3 3a1 1 0 0 1 -1 -1a3 3 0 0 1 3 -3z" />
                                </svg>
                                Sign in with Apple
                            </button>
                        </div>
                        
                        <div className="text-center my-4">
                            <span className="text-muted opacity-50">Admin mobile login only</span>
                        </div>
                        
                        {/* Login Form - Mobile Field */}
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4 control-group">
                                <label className="form-label">Admin Mobile Number</label>
                                <div className="input-group">
                                    <span className="input-group-text">📱</span>
                                    <input 
                                        type="tel" 
                                        className="form-control" 
                                        placeholder="+91-9490633344"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value)}
                                        required
                                        pattern="^\+[0-9]{1,3}-[0-9]{10}$"
                                    />
                                </div>
                                <small className="text-muted mt-1 d-block">
                                    Format: +[country code]-[10 digit number]
                                </small>
                            </div>
                            
                            <div className="d-grid mt-4">
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span className="d-flex align-items-center justify-content-center gap-2">
                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                            Sending OTP...
                                        </span>
                                    ) : 'Send OTP'}
                                </button>
                            </div>
                        </form>
                        
                        <div className="mt-3 text-center">
                            <p className="text-muted small">
                                <strong>Admin Access Only</strong> - Other users not permitted
                            </p>
                        </div>
                        
                        <div className="text-center mt-3">
                            <span className="text-muted">For admin access issues, contact: </span>
                            <Link to="#">Administrator</Link>
                        </div>
                    </div>
                </div>
                
                {/* Right Side: Image */}
                <div className="col-lg-6 d-none d-lg-block p-0 overflow-hidden">
                    <div className="d-flex justify-content-start">
                        <img 
                            style={{ objectFit: 'cover', height: '100vh' }} 
                            src={loginImage} 
                            alt="Login illustration" 
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}